import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Outlet, Navigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/auth.store';
import { useRefreshProfile } from '../../features/auth';
import {
  getFirstAdminPath,
  hasAdminPermission,
  type AdminPermissionKey,
} from '../../lib/adminPermissions';

export function ProtectedRoute() {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const refreshProfile = useRefreshProfile();

  // On mount, refresh user profile if we have a persisted token
  useEffect(() => {
    if (token) {
      refreshProfile();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No token → redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Token exists but user is not admin (shouldn't happen if login flow is correct,
  // but guard defensively)
  if (user && user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

interface RequireAdminPermissionProps {
  permission: AdminPermissionKey;
  children: ReactNode;
}

export function RequireAdminPermission({ permission, children }: RequireAdminPermissionProps) {
  const user = useAuthStore((s) => s.user);

  if (!hasAdminPermission(user, permission)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="text-sm font-medium uppercase tracking-[0.24em] text-gray-400">Permission</div>
          <h2 className="mt-3 text-2xl font-semibold text-gray-900">无权限访问此页面</h2>
          <p className="mt-3 text-sm leading-6 text-gray-500">
            当前账号未被授予此页面权限。请联系主账号调整子账号授权范围。
          </p>
          <div className="mt-6">
            <Link
              to={getFirstAdminPath(user)}
              className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
            >
              返回可访问页面
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
